import type { AppDefinition } from '@/types/app';

/**
 * The versions a web app's source has been, so its owner can take back the last change.
 *
 * A chat edit costs credits and goes live by itself, and "add a club accuracy chart" can come
 * back as something the owner did not want. Until this existed the only way back was to
 * describe the old app in a new sentence — another paid edit, and a model asked to reproduce
 * something it no longer has in front of it. The source before each change is the only exact
 * way back, so it is kept.
 *
 * On the DEFINITION, as `editHistory`, because the definition is what both clients already
 * store and read by id: the phone's SQLite row and the browser's IndexedDB record. A history
 * kept anywhere else would be one more thing to lose track of when the app is opened on the
 * other client or after a restart.
 *
 * Pure — no IO, no state — so the phone and the web builder keep the same history the same way.
 * (The web client may import it: see web/src/__tests__/boundary.test.ts.)
 *
 * WHAT IS KEPT: the last FIVE, newest last. Every version is a whole app body, and the record
 * is read and written whole on every save, so an unbounded history would make every save of a
 * long-lived app slower and larger than the app itself. Five covers "undo that, and the one
 * before" with room to spare.
 */

/** One earlier version: the source as it was BEFORE a change, and what that change was. */
export interface EditVersion {
  /** The whole app body before the change. What an undo puts back. */
  source: string;
  /** When the change was made (ISO). */
  at: string;
  /** What was asked for — the chat sentence, or what the Manage tab changed. */
  instruction: string;
}

/** How many earlier versions a record keeps. */
export const MAX_VERSIONS = 5;

/*
 * `editHistory` is read through this widening rather than through AppDefinition directly, so
 * the module compiles whether or not the field has been declared on the type yet — and keeps
 * compiling once it has.
 */
type WithHistory = { editHistory?: unknown };

/**
 * The versions a record holds, oldest first — only well-formed ones.
 *
 * A record read back from storage is whatever was written, by whichever version of either
 * client wrote it. An entry with no source is dropped rather than trusted: restoring it would
 * put an EMPTY app on the live page, and that is the one outcome an undo must never have.
 */
function versionsOf(definition: AppDefinition): EditVersion[] {
  const raw = (definition as WithHistory).editHistory;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (v): v is EditVersion =>
      !!v &&
      typeof v === 'object' &&
      typeof (v as EditVersion).source === 'string' &&
      (v as EditVersion).source.trim().length > 0,
  ).map((v) => ({
    source: v.source,
    at: typeof v.at === 'string' ? v.at : '',
    instruction: typeof v.instruction === 'string' ? v.instruction : '',
  }));
}

/**
 * Record the source as it was BEFORE a change, and return the definition with it kept.
 *
 * Call it with the record as it stands and the source that is about to be replaced; then lay
 * the new source over what comes back. The definition's own `source` is not touched here.
 *
 * A version with no source is not recorded — there is nothing to go back to, and restoring
 * nothing would blank the app. Neither is one identical to the newest kept version, which
 * would make the next undo change nothing while claiming to.
 */
export function pushVersion<T extends AppDefinition>(definition: T, version: EditVersion): T {
  if (typeof version.source !== 'string' || !version.source.trim()) return definition;
  const kept = versionsOf(definition);
  const newest = kept[kept.length - 1];
  if (newest && newest.source === version.source) return definition;
  const next = [...kept, { source: version.source, at: version.at, instruction: version.instruction }];
  return { ...definition, editHistory: next.slice(-MAX_VERSIONS) } as T;
}

/** The newest kept version — what "Undo last change" would restore — or null when there is none. */
export function lastVersion(definition: AppDefinition): EditVersion | null {
  const kept = versionsOf(definition);
  return kept.length ? kept[kept.length - 1] : null;
}

/**
 * Take the newest version back off the record and put its source back.
 *
 * Returns the definition with that version's source restored and the version removed, and the
 * version itself (for what to tell the owner). With nothing kept, the definition comes back
 * unchanged and `version` is null — the caller says there is nothing to undo.
 */
export function popVersion<T extends AppDefinition>(definition: T): { definition: T; version: EditVersion | null } {
  const kept = versionsOf(definition);
  const version = kept.pop() ?? null;
  if (!version) return { definition, version: null };
  return { definition: { ...definition, source: version.source, editHistory: kept } as T, version };
}
